import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { actions as followingActions, getSuggestedCommunities } from "../../../../actions/following";
import styles from "./suggestedCommunities.module.css";

// reason_code → translation key; the backend's English `reason` is the fallback for a new code.
const REASON_KEYS = {
  heritage: "dashboard_feed_suggested_reason_heritage",
  location: "dashboard_feed_suggested_reason_location",
  practice: "dashboard_feed_suggested_reason_practice",
  popular: "dashboard_feed_suggested_reason_popular"
};

/**
 * The empty My Feed's onboarding grid (GROWTH-7135): up to 6 communities to follow, each with a
 * one-tap Follow, from GET /api-v2/feed/suggested-communities. Renders nothing while loading, on
 * failure, or when there is nothing to suggest, so the feed's own empty state stands alone.
 *
 * One tap follows the member's home city (or state) first when they follow no location —
 * PUT /user-following/communities rejects a save until one is followed — then the community
 * (followingActions.followSuggestedCommunity). "Browse all communities" keeps the /following page.
 *
 * @param {Object} props
 * @param {Function} props.onFollowed () => void — called after a follow succeeds so the feed reloads inline
 * @param {Function} [props.onAvailabilityChange] (hasCommunities: boolean) => void — whether the grid shows anything
 */
const SuggestedCommunities = ({ onFollowed, onAvailabilityChange }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [suggestions, setSuggestions] = useState(null);
  const [pendingId, setPendingId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    getSuggestedCommunities(controller.signal)
      .then(data => setSuggestions(data))
      .catch(() => {
        // A failed or aborted load leaves the plain empty state; nothing to retry here.
      });

    return () => controller.abort();
  }, []);

  const count = suggestions ? suggestions.communities.length : 0;
  useEffect(() => {
    if (onAvailabilityChange) onAvailabilityChange(count > 0);
  }, [count, onAvailabilityChange]);

  const handleFollow = useCallback(
    async community => {
      if (pendingId !== null) return;
      setPendingId(community.heritage_id);
      setError(null);
      try {
        await dispatch(followingActions.followSuggestedCommunity(community.heritage_id, suggestions.home_location));
        setSuggestions(current => ({
          ...current,
          communities: current.communities.filter(item => item.heritage_id !== community.heritage_id)
        }));
        if (onFollowed) onFollowed();
      } catch (failure) {
        setError(failure?.message || t("dashboard_feed_suggested_follow_error"));
      } finally {
        setPendingId(null);
      }
    },
    [dispatch, onFollowed, pendingId, suggestions, t]
  );

  if (!suggestions || !suggestions.communities.length) return null;

  return (
    <section className={styles.suggested} aria-labelledby="feed-suggested-title">
      <h2 className={styles.title} id="feed-suggested-title">
        {t("dashboard_feed_suggested_title")}
      </h2>
      <p className={styles.body}>{t("dashboard_feed_suggested_body")}</p>

      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}

      <ul className={styles.grid}>
        {suggestions.communities.map(community => (
          <li className={styles.card} key={community.heritage_id}>
            <span className={styles.name}>{community.title}</span>
            <span className={styles.reason}>
              {REASON_KEYS[community.reason_code] ? t(REASON_KEYS[community.reason_code]) : community.reason}
            </span>
            <span className={styles.members}>{t("dashboard_feed_suggested_members", { count: community.member_count })}</span>
            <button
              type="button"
              className={styles.follow}
              disabled={pendingId !== null}
              aria-label={t("dashboard_feed_suggested_follow_aria", { name: community.title })}
              onClick={() => handleFollow(community)}
            >
              {pendingId === community.heritage_id ? t("dashboard_feed_suggested_following") : t("dashboard_feed_suggested_follow")}
            </button>
          </li>
        ))}
      </ul>

      <Link to="/following" className={styles.browse}>
        {t("dashboard_feed_suggested_browse")}
      </Link>
    </section>
  );
};

export default SuggestedCommunities;
